// One authoritative envelope per mounted Inspector surface. Requests are scoped
// to the selected document; a slow refresh cannot overwrite a newer mutation.
import { useCallback, useEffect, useRef, useState } from 'react';
import { parseApiErrorBody } from '@/api/v2Client';
import { documentNavigation, type NavigationEnvelope } from '@/api/documentNavigation';

export interface UseNavigationEnvelopeResult {
  readonly env: NavigationEnvelope | null;
  readonly loading: boolean;
  readonly loadError: string | null;
  readonly refetch: () => Promise<void>;
  readonly setEnv: (next: NavigationEnvelope) => void;
}
interface EnvelopeState {
  documentId: string;
  env: NavigationEnvelope | null;
  loading: boolean;
  loadError: string | null;
}
export function useNavigationEnvelope(documentId: string): UseNavigationEnvelopeResult {
  const [state, setState] = useState<EnvelopeState>({ documentId, env: null, loading: true, loadError: null });
  const request = useRef(0);
  const mounted = useRef(false);
  const selectedDocument = useRef(documentId);
  selectedDocument.current = documentId;

  const refetch = useCallback(async (): Promise<void> => {
    const epoch = ++request.current;
    const current = () => mounted.current && epoch === request.current && selectedDocument.current === documentId;
    setState((previous) => ({ documentId, env: previous.documentId === documentId ? previous.env : null, loading: true, loadError: null }));
    try {
      const next = await documentNavigation.get(documentId);
      if (!current()) return;
      if (next.documentId.toLowerCase() !== documentId.toLowerCase()) throw new Error('The navigation response did not match the selected document.');
      setState({ documentId, env: next, loading: false, loadError: null });
    } catch (err) {
      if (!current()) return;
      const body = parseApiErrorBody(err);
      setState((previous) => ({ ...previous, loading: false, loadError: body?.message ?? body?.error ?? (err instanceof Error ? err.message : String(err)) }));
    }
  }, [documentId]);

  useEffect(() => {
    mounted.current = true;
    void refetch();
    return () => {
      mounted.current = false;
      // Invalidate the live request counter, including refreshes begun after setup.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      request.current++;
    };
  }, [refetch]);

  const setEnv = useCallback((next: NavigationEnvelope): void => {
    if (!mounted.current || selectedDocument.current !== documentId || next.documentId.toLowerCase() !== documentId.toLowerCase()) return;
    request.current++; // A mutation response supersedes an older in-flight GET.
    setState({ documentId, env: next, loading: false, loadError: null });
  }, [documentId]);

  const current = state.documentId === documentId;
  return { env: current ? state.env : null, loading: current ? state.loading : true, loadError: current ? state.loadError : null, refetch, setEnv };
}
